/* eslint-disable @typescript-eslint/no-unused-vars */
// src/components/subscriptions/SubscriptionOptions.jsx
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, Button, Tabs, Row, Col, Tag, Typography } from 'antd'
import { Fade } from 'react-awesome-reveal'

import { isMobile } from 'react-device-detect'

import { useStores } from '../../../cls/stores/MobXProvider'
import {
	stripeCheckout,
	stripeLoadPlans
} from '../../../stores/subscriptions/actions'
import { observer } from 'mobx-react-lite'
import SubscriptionCancelButton from './SubscriptionActions'
import { Link } from 'react-router-dom'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import ServicesOverview from '../../general/HomePage/ServicesOverview'
import StripeService from '../../../api/modules/api-stripe'
const { Paragraph } = Typography

const SubscriptionOptions = () => {
	const { users, subscriptions } = useStores()
	const plans = subscriptions.plans
	const { t } = useSmartTranslation()
	const { loggedInUserId } = users
	const user = users.requireUserById(loggedInUserId)
	const userEmail = user.email
	const userSubscriptions = user.temporarySubscriptions

	/* ---------- helpers ---------- */
	const getActiveSubscription = (id) => {
		const activeSubscription = userSubscriptions.find(
			(subscription) => subscription?.plan?.id === id
		)
		return activeSubscription
			? {
					subId: activeSubscription.id,
					status: activeSubscription.status,
					cancel_at_period_end:
						activeSubscription?.cancel_at_period_end
			  }
			: { subId: null, status: null, cancel_at_period_end: null }
	}

	const formatPrice = (amount, currency) => {
		const formatted = (amount / 100).toFixed(2)
		return formatted.endsWith('.00')
			? `${formatted.slice(0, -3)} ${currency.toUpperCase()}`
			: `${formatted} ${currency.toUpperCase()}`
	}

	useEffect(() => {
		// StripeService.stripeProductsSync()
		// StripeService.stripeProductsSyncMeta()
		plans.length === 0 && stripeLoadPlans()
	}, [])

	/* ---------- ui helpers ---------- */
	const SubscriptionDetailsRoutes = {
		'Web Design': '/services/programming-tech/wordpress',
		Programming: '/services/programming-tech',
		Marketing: '/services/digital-marketing',
		'Graphic Design': '/services/graphics-design',
		'Content Writing': '/services/writing-translation'
	}

	const RenderTabsContent = ({ children, onePerRow = false }) => {
		return (
			<Row gutter={[16, 16]} style={{ marginTop: 30 }}>
				{children.map((child, i) => (
					<Col key={i} span={isMobile || onePerRow ? 24 : 8}>
						{child}
					</Col>
				))}
			</Row>
		)
	}
	RenderTabsContent.propTypes = {
		children: PropTypes.array,
		onePerRow: PropTypes.bool
	}

	/* ---------- core ---------- */
	const renderPlanDetails = (plan) => {
		const { subId, status, cancel_at_period_end } = getActiveSubscription(
			plan.id
		)

		const isRecurring =
			plan.product?.metadata?.recurring !== 'false' &&
			plan.product?.metadata?.recurring !== false

		const isActive = status === 'active' && isRecurring
		const tagColor = isActive ? 'green' : 'default'
		const borderColor = isActive ? 'green' : 'default'
		const detailPath =
			plan.product?.metadata?.path ??
			SubscriptionDetailsRoutes[plan.product?.metadata?.category]
		const cat = plan.product?.metadata?.category

		const isBackline = cat === 'Backline'

		const BacklineWrapper = ({ children }) => {
			if (!isBackline) return children
			return (
				<ServicesOverview
					title="Backline Service Overview"
					description=""
					disableSections={{
						title: false,
						spacing: true
					}}
					wrapperStyles={{
						padding: '20px'
					}}
				>
					<div
						style={{
							border: '1px solid rgb(72 70 245)',
							borderRadius: '8px',
							padding: '20px',
							backgroundColor: '#4846f41f'
						}}
					>
						{children}
					</div>

					<Fade triggerOnce direction="up" damping={0.15}>
						<Paragraph
							style={{
								marginTop: 50,
								marginBottom: 24,
								fontSize: '16px',
								textAlign: isMobile ? 'left' : 'center'
							}}
						>
							Backline is our premium service designed for
							high-demand clients. It offers a dedicated team of
							experts from all areas of our expertise, from web
							design to content writing, under one roof.
						</Paragraph>
					</Fade>
				</ServicesOverview>
			)
		}
		BacklineWrapper.propTypes = {
			plan: PropTypes.object.isRequired,
			children: PropTypes.node.isRequired
		}
		return (
			<Card key={plan.id} style={{ borderColor }}>
				<BacklineWrapper plan={plan}>
					{isActive && (
						<Tag
							color={tagColor}
							style={{ position: 'absolute', top: 16, right: 16 }}
						>
							{status}
						</Tag>
					)}
					<p>
						<strong>{t('single.plan')}:</strong> {plan.product.name}
					</p>
					<p>
						<strong>{t('single.amount')}</strong>{' '}
						{formatPrice(plan.unit_amount, plan.currency)}
					</p>
					<p>
						<strong>{t('single.billing-interval')}</strong>{' '}
						{isRecurring && plan.recurring?.interval
							? `${plan.recurring.interval_count} ${plan.recurring.interval}(s)`
							: t('single.one-time')}
					</p>
					<p>
						<strong>{t('single.description')}</strong>{' '}
						{plan.product.description}
					</p>
					{detailPath && (
						<p>
							<strong>{t('single.service-details')} </strong>
							<Link to={detailPath}>
								{t('home.cardKnowMore')}
							</Link>
						</p>
					)}
					{plan.nickname && (
						<p>
							<strong>{t('single.note')}</strong> {plan.nickname}
						</p>
					)}
					{/* action buttons */}
					{isRecurring && isActive && subId ? (
						cancel_at_period_end ? (
							<SubscriptionCancelButton
								type="resume"
								subId={subId}
								status={status}
							/>
						) : (
							<SubscriptionCancelButton
								type="cancel"
								subId={subId}
								status={status}
							/>
						)
					) : (
						<Button
							type="primary"
							onClick={() =>
								stripeCheckout(
									userEmail,
									plan.id,
									plan.product.metadata.category
								)
							}
						>
							{isRecurring
								? t('single.subscribe')
								: t('single.buy')}
						</Button>
					)}
				</BacklineWrapper>
			</Card>
		)
	}

	/* ---------- data grouping ---------- */
	const filterPlansByType = (type) =>
		plans
			.filter((p) => p.product?.metadata?.type)
			.filter(
				(p) =>
					p.product.metadata.type.toLowerCase() === type.toLowerCase()
			)

	const getUniqueTypes = () => [
		...new Set(plans.map((p) => p.product?.metadata?.type).filter(Boolean))
	]

	const getUniqueCategories = () => [
		...new Set(plans.map((p) => p.product.metadata.category))
	]

	const renderTabs = (type) => {
		const filtered = filterPlansByType(type)
		const categories = getUniqueCategories().filter((cat) =>
			filtered.some((p) => p.product.metadata.category === cat)
		)

		const items = categories.map((cat, i) => ({
			key: `${i}`,
			label: cat === 'Backline' ? <Tag color="#4846f4">{cat}</Tag> : cat,
			children: (
				<RenderTabsContent onePerRow={cat === 'Backline'}>
					{filtered
						.filter((p) => p.product.metadata.category === cat)
						.map(renderPlanDetails)}
				</RenderTabsContent>
			)
		}))

		return (
			<Tabs
				className="ant-tabs-nav-before-no-border"
				centered
				items={items}
			/>
		)
	}

	/* ---------- render ---------- */
	return (
		<Tabs
			className="ant-tabs-nav-no-margin"
			centered
			defaultActiveKey="0"
			items={getUniqueTypes().map((type, i) => ({
				key: `${i}`,
				label: type,
				children: renderTabs(type)
			}))}
		/>
	)
}

export default observer(SubscriptionOptions)
